import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./signup.css";
import { axiosInstance } from "../../services/axiosInstance";
import { endpoints } from "../../services/endpoint";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Modal } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

const Signuppage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    UserName: "",
    Password: "",
    CompanyName: "",
    Email: "",
    PhoneNumber: "",
    Communication: "",
    // BestWayToReach: "",
    AWSAccountNumber: "",
    InstanceARN: "",
  });

  // Handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  // Validation
  const validate = () => {
    let tempErrors = {};

    if (!formData.UserName.trim()) tempErrors.UserName = "User name is required";
    if (!formData.Password.trim()) tempErrors.Password = "Last name is required";
    if (!formData.CompanyName.trim()) tempErrors.CompanyName = "Company name is required";

    if (!formData.Email.trim()) {
      tempErrors.Email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      tempErrors.Email = "Enter a valid email address";
    }

    if (!formData.PhoneNumber.trim()) {
      tempErrors.PhoneNumber = "Phone number is required";
    } else if (!/^\+1\d{10}$/.test(formData.PhoneNumber)) {
      tempErrors.PhoneNumber = "Format should be +1XXXXXXXXXX";
    }


    if (!formData.Communication) tempErrors.Communication = "Please select an option";

    if (!formData.AWSAccountNumber.trim()) {
      tempErrors.AWSAccountNumber = "AWS Account Number is required";
    } else if (!/^\d{12}$/.test(formData.AWSAccountNumber)) {
      tempErrors.AWSAccountNumber = "AWS Account Number must be 12 digits";
    }

    if (!formData.InstanceARN.trim()) {
      tempErrors.InstanceARN = "Instance ARN is required";
    } else {
      const instanceArnRegex = /^arn:aws:connect:[a-z0-9-]+:\d{12}:instance\/[a-zA-Z0-9-]+$/;
      if (!instanceArnRegex.test(formData.InstanceARN)) {
        tempErrors.InstanceARN =
          "Invalid ARN format. Expected: arn:aws:connect:region:account-id:instance/instance-id";
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSignup = async () => {
    if (!validate()) return;
    setLoading(true);

    try {
      await axiosInstance.post(endpoints.signup.signuplogin, formData);
      // await axiosInstance.post(endpoints.signupEmail.signuploginEmail, formData);

      setSnackbar({
        open: true,
        message: "Form Submitted Successfully!!",
        severity: "success",
      });

      // Show success modal
      setSuccessModalOpen(true);

      setTimeout(() => {
        setSuccessModalOpen(false);
        navigate("/");
      }, 10000); // 10 seconds

    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          "Signup failed. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <Typography className="signup-title">Create Account</Typography>

        {/* Row 1: First + Last Name */}
        <Box className="signup-form-grid">
          <TextField
            label="First Name"
            name="FirstName"
            value={formData.FirstName}
            onChange={handleChange}
            error={!!errors.FirstName}
            helperText={errors.FirstName}
            fullWidth
          />
          <TextField
            label="Last Name"
            name="LastName"
            value={formData.LastName}
            onChange={handleChange}
            error={!!errors.LastName}
            helperText={errors.LastName}
            fullWidth
          />
        </Box>
        
        {/* Row 1: User Name + Password */}
        <Box className="signup-form-grid">
          <TextField
            label="User Name"
            name="UserName"
            value={formData.UserName}
            onChange={handleChange}
            error={!!errors.UserName}
            helperText={errors.UserName}
            fullWidth
          />
          <TextField
            label="Password"
            name="Password"
            type={showPassword ? "text" : "password"}
            value={formData.Password}
            onChange={handleChange}
            error={!!errors.Password}
            helperText={errors.Password}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Row 2: Company + Email */}
        <Box className="signup-form-grid">
          <TextField
            label="Company Name"
            name="CompanyName"
            value={formData.CompanyName}
            onChange={handleChange}
            error={!!errors.CompanyName}
            helperText={errors.CompanyName}
            fullWidth
          />
          <TextField
            label="Email"
            name="Email"
            type="email"
            value={formData.Email}
            onChange={handleChange}
            error={!!errors.Email}
            helperText={errors.Email}
            fullWidth
          />
        </Box>

        {/* Row 3: Phone + Best Way to Reach */}
        <Box className="signup-form-grid">
          <TextField
            label="Phone Number"
            name="PhoneNumber"
            placeholder="+1XXXXXXXXXX"
            value={formData.PhoneNumber}
            onChange={handleChange}
            error={!!errors.PhoneNumber}
            helperText={errors.PhoneNumber}
            fullWidth
          />
          <TextField
            label="Best Way To Reach"
            name="Communication"
            select
            value={formData.Communication}
            onChange={handleChange}
            error={!!errors.Communication}
            helperText={errors.Communication}
            fullWidth
          >
            <MenuItem value="Phone">Phone</MenuItem>
            <MenuItem value="Email">Email</MenuItem>
          </TextField>
        </Box>

        {/* Row 4: AWS Account + Instance ARN */}
        <Box className="signup-form-grid">
          <TextField
            label="AWS Account Number"
            name="AWSAccountNumber"
            value={formData.AWSAccountNumber}
            onChange={handleChange}
            error={!!errors.AWSAccountNumber}
            helperText={errors.AWSAccountNumber}
            fullWidth
          />
          <TextField
            label="Instance ARN"
            name="InstanceARN"
            value={formData.InstanceARN}
            onChange={handleChange}
            error={!!errors.InstanceARN}
            helperText={errors.InstanceARN}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      <div style={{ fontSize: "13px", lineHeight: "1.5" }}>
                        <strong>How to get Instance ARN:</strong>
                        <br />
                        1. Login to AWS Console
                        <br />
                        2. Open Amazon Connect
                        <br />
                        3. Select your instance
                        <br />
                        4. Go to “Distribution settings”
                        <br />
                        5. Copy the Instance ARN
                        <br />
                        6. Paste it here
                      </div>
                    }
                  >
                    <IconButton edge="end" size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Button
          variant="contained"
          className="signup-btn"
          onClick={handleSignup}
          disabled={loading}

        >
          {loading ? "Submitting Form ..." : "Submit Form"}
        </Button>

        <Button
          variant="text"
          className="back-login-btn"
          fullWidth
          onClick={() => navigate("/")}
        >
          Back to Login
        </Button>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
        <Modal open={successModalOpen} onClose={() => setSuccessModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "#fff",
              p: 4,
              borderRadius: 3,
              width: 420,
              textAlign: "center",
            }}
          >
            {/* SUCCESS ICON */}
            <CheckCircleIcon
              sx={{
                fontSize: 70,
                color: "#2e7d32", // green
                mb: 1,
              }}
            />

            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Thank You!
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Thank you. We’ve received your details, and one of our integration specialists will reach out to you within the next 24 hours.
            </Typography>

            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={() => setSuccessModalOpen(false)}
            >
              OK
            </Button>
          </Box>
        </Modal>
      </div>
    </div>
  );
};

export default Signuppage;
